import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate del Mago L6-10 (manuale V1, rr. 4272-4392). 14 mosse.
 *
 * Pattern notabili:
 *  - **Prima applicazione di `MoveSchema.replaces`** (mai usato finora):
 *    - *Magia Potenziata Superiore* sostituisce *Magia Potenziata*
 *    - *Estremamente Logico* sostituisce *Logico*
 *  - **5 mosse richiedono *Lanciare Incantesimi Arcani***
 *  - **Catene di prerequisiti**: *Mago da Battaglia* → *Mago da Guerra*;
 *    *Protezione Arcana* → *Armatura Arcana*; *Magia Improvvisata* → *Arte
 *    dell'Improvvisazione*; *Vediamo come Funzioni* → *Laboratorio*;
 *    *Artefice* → *Alterare le Trame*.
 *  - **Cross-class restriction in description** ("se hai già messo le tue
 *    zampacce su questa mossa come Chierico" su *Magia Potenziata Superiore*).
 *  - ***Praticante Magico***: mossa multiclasse standard, `tag: 'multiclasse'`.
 */

const arteDellImprovvisazione: Move = {
  id: 'mago.arte-dell-improvvisazione',
  name: "Arte dell'Improvvisazione",
  type: 'advanced_6_10',
  classId: 'mago',
  trigger: "Quando scegli le condizioni per ottenere l'effetto voluto con *Magia Improvvisata*",
  description: 'Ne devi scegliere una in meno del normale.',
  tags: [],
  prerequisites: ['mago.magia-improvvisata'],
  replaces: [],
};

const magoDaGuerra: Move = {
  id: 'mago.mago-da-guerra',
  name: 'Mago da Guerra',
  type: 'advanced_6_10',
  classId: 'mago',
  trigger: 'Quando usi *Mago da Battaglia*',
  description:
    "Con 7+, infliggi 1d10 danni con etichetta *fisico* e, con 12+, scegli quattro. Inoltre, aggiungi queste opzioni alla lista:\n\n- scegli un'etichetta di portata per l'attacco (*lunga* o *lontano*);\n- applichi l'etichetta *brutale* al colpo;\n- colpisci tutti i nemici entro distanza *lunga* dal bersaglio, ma il danno è dimezzato (per eccesso).\n\nSe hai *Protezione Arcana*, puoi ora causare 1d10 danni con etichetta *fisico* se scegli l'opzione di *difendere* per infliggere danni.",
  tags: [],
  prerequisites: ['mago.mago-da-battaglia'],
  replaces: [],
};

const armaturaArcana: Move = {
  id: 'mago.armatura-arcana',
  name: 'Armatura Arcana',
  type: 'advanced_6_10',
  classId: 'mago',
  trigger: 'Quando *difendi* qualcuno o qualcosa',
  description:
    'Hai +3 Armatura contro gli attacchi magici invece di +2, e quando ***difendi*** **qualcuno o qualcosa**, sia tu che il bersaglio (se diverso da te stesso) guadagnate +1 Armatura contro gli attacchi magici.\n\nInfine, puoi usare *Scudo Magico* anche su ciò che stai *difendendo*, ma dovrai spendere una presa di *Mana* e tirare separatamente se lo usi anche su te stesso.',
  tags: [],
  prerequisites: ['mago.protezione-arcana'],
  replaces: [],
};

const inUnaBotteDiMana: Move = {
  id: 'mago.in-una-botte-di-mana',
  name: 'In una Botte di Mana',
  type: 'advanced_6_10',
  classId: 'mago',
  trigger: 'Quando tiri per *lanciare incantesimi*',
  description:
    'Con 12+, guadagni una presa di *Mana*. Non puoi avere più prese di *Mana* del massimo consentito per il tuo livello da Incantatore.',
  tags: [],
  prerequisites: ['mago.lanciare-incantesimi-arcani'],
  replaces: [],
};

const magiaPotenziataSuperiore: Move = {
  id: 'mago.magia-potenziata-superiore',
  name: 'Magia Potenziata Superiore',
  type: 'advanced_6_10',
  classId: 'mago',
  trigger: 'Quando tiri per *lanciare incantesimi*',
  description:
    "Con 10+, se accetti di subire una complicazione dalla lista del 7-9, scegli un potenziamento tra questi (se applicabile, e con conseguenze a discrezione del GM):\n\n- gli effetti numerici sono massimizzati, gli altri potenziati;\n- l'effetto interessa un numero doppio di bersagli o un'area più vasta.\n\nCon un 12+, puoi sceglierne uno senza dover subire complicazioni.\n\nSe hai già messo le tue zampacce su questa mossa come Chierico, non puoi prenderla di nuovo!",
  tags: [],
  prerequisites: [],
  replaces: ['mago.magia-potenziata'],
};

const autoalimentato: Move = {
  id: 'mago.autoalimentato',
  name: 'Autoalimentato',
  type: 'advanced_6_10',
  classId: 'mago',
  trigger:
    'Quando hai il tempo, i materiali mistici necessari e un posto sicuro (un tuo *Laboratorio* lo è sempre)',
  description:
    'Puoi creare un luogo di potere personale. Descrivi al GM di che tipo di potere si tratta e come lo stai legando a tale luogo. Il GM ti dirà un tipo di creatura che è interessata a quel che stai facendo.\n\nQuesti luoghi di potere sono sempre abbastanza potenti da trarne beneficio. Ma stai ben attento: la magia che li permea potrebbe non essere del tipo giusto a fare quello che vuoi.',
  tags: [],
  prerequisites: ['mago.lanciare-incantesimi-arcani'],
  replaces: [],
};

const burattinoMistico: Move = {
  id: 'mago.burattino-mistico',
  name: 'Burattino Mistico',
  type: 'advanced_6_10',
  classId: 'mago',
  trigger: 'Quando usi la magia per controllare le azioni di qualcuno',
  description:
    'Egli non ricorderà nulla di ciò che gli hai fatto fare e non serberà rancore, ma tu subirai un -1 al prossimo tentativo di controllarne le azioni. Funziona con qualsiasi effetto magico (incantesimo o altro) ti permetta di controllare *direttamente* le azioni di qualcuno.\n\nQuel -1? È un fenomeno ottadimensionale che disturberà ogni tuo ulteriore tentativo di controllo, non importa quale metodo magico userai.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const legameArmonico: Move = {
  id: 'mago.legame-armonico',
  name: 'Legame Armonico',
  type: 'advanced_6_10',
  classId: 'mago',
  trigger: 'Quando lanci un incantesimo su di un personaggio con cui hai almeno un Legame attivo',
  description:
    'Se vuoi, puoi considerare di aver ottenuto un risultato di 10+ senza bisogno di tirare i dadi.\n\nDopo che hai usato *Legame Armonico* una volta, non puoi farvi ancora ricorso finché non usi nuovamente la mossa *Mana*.',
  tags: [],
  prerequisites: ['mago.lanciare-incantesimi-arcani'],
  replaces: [],
};

const catenaAstrale: Move = {
  id: 'mago.catena-astrale',
  name: 'Catena Astrale',
  type: 'advanced_6_10',
  classId: 'mago',
  trigger: 'Quando passi abbastanza tempo con un soggetto volontario o inerme',
  description:
    'Puoi legarlo a te con una *Catena Astrale*. Percepisci ciò che lui percepisce, e puoi *discernere realtà* a proposito di lui o di ciò che lo circonda, non importa la distanza.\n\nPuoi comunicare con chi si è legato a te volontariamente come se foste nella stessa stanza.\n\nNon è possibile mantenere attiva più di una *Catena Astrale* per volta.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const artefice: Move = {
  id: 'mago.artefice',
  name: 'Artefice',
  type: 'advanced_6_10',
  classId: 'mago',
  trigger:
    'Quando hai alcuni giorni di tempo e la tranquillità necessaria a provare a realizzare un oggetto magico (partendo da materiali base appropriati) o a dar luogo a un effetto magico permanente',
  description:
    "Spiega cosa vuoi ottenere e concorda con il GM, prendendo a spunto gli incantesimi esistenti, un livello (0, 1, 3, 5, 7 o 9). Se il tuo livello da Incantatore è inferiore a quello concordato, *non* potrai ottenere l'effetto voluto. Altrimenti, scegli un numero di condizioni pari a 1 più la metà del livello stabilito (arrotondando per eccesso) tra le seguenti. Puoi anche scegliere la stessa più volte, salvo che non sia indicato diversamente:\n\n- subisci una Debilitazione a tua scelta (puoi scegliere questa opzione una volta per ogni Debilitazione che non hai segnato);\n- impieghi alcune settimane (mesi se scegli quest'opzione due volte);\n- attingi alla magia di un luogo di potere in cui sei (una sola volta);\n- sacrifichi un raro ingrediente o un oggetto magico (descrivi quale);\n- ti fai aiutare da qualcuno (descrivi da chi e come);\n- l'oggetto ha solo 3 utilizzi (1 se scegli questa opzione due volte).\n\nSe il GM approva le tue scelte, ottieni ciò che volevi.\n\nQuando **non puoi metterci tutto quel tempo per realizzare l'incantamento**, ti basta qualche ora. Individua il livello e scegli le condizioni, poi tira come per *lanciare incantesimi*. Con 10+, tutto funziona come volevi. Con 7-9, il GM ti dirà un effetto collaterale, una strana limitazione, un difetto intrinseco o come l'incantamento ha turbato la trama stessa della magia.",
  tags: [],
  prerequisites: ['mago.lanciare-incantesimi-arcani'],
  replaces: [],
};

const alterareLeTrame: Move = {
  id: 'mago.alterare-le-trame',
  name: 'Alterare le Trame',
  type: 'advanced_6_10',
  classId: 'mago',
  trigger:
    'Quando hai qualche minuto di tempo e la tranquillità necessaria a manipolare il potere che risiede in un oggetto magico o in una magia permanente',
  description:
    "Spendi una presa di *Mana* e tira come per *lanciare incantesimi*. Con 10+, ottieni l'effetto sperato (distorsione, modifica del livello di potere, ecc.). Con 7-9, ottieni comunque ciò che volevi, ma la manipolazione avrà degli effetti collaterali inattesi (a discrezione del GM).\n\nPuoi modificare in modo permanente le magie più deboli (comparabili con gli effetti di un incantesimo di livello 5), ma le altre torneranno come prima dopo non molto tempo.",
  tags: [],
  prerequisites: ['mago.artefice'],
  replaces: [],
};

const laboratorio: Move = {
  id: 'mago.laboratorio',
  name: 'Laboratorio',
  type: 'advanced_6_10',
  classId: 'mago',
  trigger:
    'Quando disponi di tempo, di un luogo sicuro, e dei fondi necessari (a discrezione del GM), o quando hai il tempo e la tranquillità necessaria per provare a realizzare un congegno meccanico',
  description:
    "Quando **disponi di tempo, di un luogo sicuro e dei fondi necessari** (a discrezione del GM), puoi creare un *Laboratorio*. Descrivilo.\n\nQuando **hai il tempo e la tranquillità necessaria per provare a realizzare un congegno meccanico**, spiega cosa vuoi ottenere. Il GM ti dirà quanto tempo occorre (minuti, ore, giorni, ecc.) e di che materiali hai bisogno (e il loro costo), sulla base delle dimensioni del congegno. Concorda poi con il GM un grado di complessità tecnica che va da 1 a 6 e scegli altrettante opzioni tra quelle della lista. Puoi anche scegliere la stessa opzione più volte, salvo che non sia indicato diversamente:\n\n- ci vuole molto tempo (ogni volta che scegli questa opzione il tempo base raddoppia);\n- sei nel tuo *Laboratorio* (puoi scegliere quest'opzione una sola volta);\n- usi un materiale o un semilavorato raro (descrivi quale);\n- impieghi più materiali base (ogni volta che scegli questa opzione il costo raddoppia);\n- ti fai aiutare da qualcuno (descrivi da chi e come);\n- il congegno ha un difetto sconosciuto (il GM lo descriverà solo quando si manifesta);\n- il congegno ha una strana limitazione (a discrezione del GM);\n- il congegno non funzionerà per molto (il GM ti dirà per quanto);\n- la costruzione del congegno causa danni a te, ai tuoi collaboratori o al posto in cui lo realizzi (il GM ti dirà come ciò si concretizza).\n\nSe il GM approva le tue scelte, costruisci il congegno che volevi.",
  tags: [],
  prerequisites: ['mago.vediamo-come-funzioni'],
  replaces: [],
};

const estremamenteLogico: Move = {
  id: 'mago.estremamente-logico',
  name: 'Estremamente Logico',
  type: 'advanced_6_10',
  classId: 'mago',
  trigger:
    'Quando *discerni la realtà*, non affidandoti alla tua intuizione o al tuo istinto, ma analizzando ciò che ti circonda con logica e deduzione',
  description:
    'Puoi tirare+**INT** invece che +**SAG**. Con un 12+, puoi fare tre domande qualsiasi al GM, anche se non sono sulla lista.',
  tags: [],
  prerequisites: [],
  replaces: ['mago.logico'],
};

const praticanteMagico: Move = {
  id: 'mago.praticante-magico',
  name: 'Praticante Magico',
  type: 'advanced_6_10',
  classId: 'mago',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Ottieni una mossa non multiclasse dalla lista di una qualunque classe. Scegli la mossa come se fossi di un livello inferiore al tuo, a meno che quella mossa non sia basata sulla magia.',
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool aperto + cap livello -1 (simmetrico a *Dilettante Magico*).
  multiclass: {
    picks: { kind: 'any-other-class' },
    levelAdjustment: { delta: -1 },
  },
};

export const magoAdvanced6to10Moves: readonly Move[] = [
  arteDellImprovvisazione,
  magoDaGuerra,
  armaturaArcana,
  inUnaBotteDiMana,
  magiaPotenziataSuperiore,
  autoalimentato,
  burattinoMistico,
  legameArmonico,
  catenaAstrale,
  artefice,
  alterareLeTrame,
  laboratorio,
  estremamenteLogico,
  praticanteMagico,
] as const;
